import { LRUCache } from 'lru-cache';

const ERROR_RETRY_INTERVAL = 10000;

const INITIAL_EXPONENTIAL_BACK_OFF_ERROR_INTERVAL = 5000;
const MAX_EXPONENTIAL_BACK_OFF_ERROR_INTERVAL = 10 * 60 * 1000; // 10 minutes

let nextSubscriberId = 1;

type CacheEntry<E> = {
  /**
   * The time this cache entry was last updated. This will only change if the
   * data in the entry changes.
   */
  fetchTime: number;
} & (
  | {
      state: 'cached';
      /** The time this cache entry was most recently revalidated. */
      validatedTime: number;
      /* The cache entry.  This will be undefined if we have previously tried
       * to fetch data, but errored out.
       */
      entry: E;
    }
  | {
      state: 'error';
      err: Error;
      /**
       * Number, in milliseconds, that we are currently waiting from the `fetchTime`
       * to retry a fetch after an error.
       */
      lastErrorDelay: number;
    }
);

interface Subscriber<E, Q, D, R> {
  id: string;
  active: boolean;
  query: Q;
  listener: (err: Error | undefined, result: R | undefined) => void;
  cacheKeys: D[];
  keys: string[];
  lastCacheEntries: (CacheEntry<E> | undefined)[] | undefined;
  /**
   * latestFetchTimeSent is the fetchTime of the highest cached result sent to
   * the subscriber.  After revalidating data, if this hasn't changed, we have
   * no new data to send to the subscriber.  This will be undefined if we've
   * never sent data.
   */
  latestFetchTimeSent: number | undefined;
}

/**
 * WOSCache (short for "Write-Only Series Cache") is the basis for the EventCache
 * and the SegmentCache.
 *
 * WOSCache caches "write-only" data collections - collections that are added to
 * (and where events can be added in the past) but which never change.  Events
 * and motion data are examples of data like this in our system.  (Events can be
 * updated, but where we only care about the duration or type of an event, the
 * EventCache is good enough.)
 *
 * Fetching data or subscribing to the cache will use a "stale-while-revalidate"
 * strategy - if we have stale data, it will be returned immediately, and then
 * we will go fetch fresh data in the background.
 *
 * See ./WOSCache.md for details about how this class works and is used.
 *
 * @param E - The format of a cache entry.
 * @param Q - The format of a query.
 * @param D - The format of a cache descriptor
 * @param R - The format for the final result.
 */
export default class WOSCache<E, Q, D, R> {
  public name: string;
  private queryMapper: (query: Q) => D[];
  private keyFunction: (descriptor: D) => string;
  private fetchFunction: (descriptor: D, oldEntry: E | undefined) => Promise<E>;
  private resultMapper: (query: Q, entries: E[]) => R;
  private getRevalidationTimeout: (descriptor: D, entry: E) => number | undefined;

  // The actual cached data.
  private _cache: LRUCache<string, CacheEntry<E>>;

  // While we're trying to fetch data, we store a Promise in _cacheWorkers, so
  // two simultaneous requests don't result in two queries to the server.
  private _cacheWorkers: { [key: string]: Promise<CacheEntry<E>> } = {};

  private _subscribers: Subscriber<E, Q, D, R>[] = [];

  /** Time the cache was last updated. */
  public lastUpdate: number;

  /**
   * The number of queries that were answered from the cache.
   */
  public cacheHits = 0;

  /**
   * The number of queries where we had to go to the server to get results.
   */
  public cacheMisses = 0;

  /**
   * The number of times we refreshed a stale cache with data from the server.
   */
  public cacheRefreshes = 0;

  /**
   * Create a new WOSCache.
   *
   * @param params.name - A name for this cache, for debug logging.
   * @param params.queryMapper - Maps a query into an array of descriptors used to
   *   fetch cache entries.
   * @param params.keyFunction - Gets the cache key for a descriptor.
   * @param params.fetchFunction - Fetches the data for a descriptor.  If this is a
   *   refresh, the `oldEntry` will be provided.  In the refresh case, if no new
   *   data is available, `fetchFunction` should return exactly the same object.
   * @param params.resultMapper - Converts a collection of cache entries into final
   *   results to pass to the client.  This gets passed both the array of cache entries,
   *   and the matching array of descriptors which generated those entries.
   * @param params.getRevalidationTimeout - Given a descriptor, returns the amount
   *   of time in ms until we need to revalidate a cache entry.  If this returns
   *   undefined, then the cache entry will never be revalidated.
   * @param max - The maximum number of entries to keep cached.
   * @param length - The "size" of each entry relative to the `max`.
   */
  constructor(params: {
    name: string;
    queryMapper: (query: Q) => D[];
    keyFunction: (descriptor: D) => string;
    fetchFunction: (descriptor: D, oldEntry: E | undefined) => Promise<E>;
    resultMapper: (query: Q, entries: E[]) => R;
    getRevalidationTimeout: (descriptor: D, entry: E) => number | undefined;
    max: number;
    length?: (entry: E) => number;
  }) {
    this.name = params.name;

    this._cache = new LRUCache<string, CacheEntry<E>>({
      max: params.max,
      maxSize: params.max,
      sizeCalculation: (value: CacheEntry<E>) => {
        if (value.state === 'cached') {
          return params.length?.(value.entry) ?? 1;
        }
        return 1;
      },
    });

    this.queryMapper = params.queryMapper;
    this.keyFunction = params.keyFunction;
    this.fetchFunction = params.fetchFunction;
    this.resultMapper = params.resultMapper;
    this.getRevalidationTimeout = params.getRevalidationTimeout;

    this.lastUpdate = Date.now();
  }

  /**
   * Returns true if this query can be satisfied from the cache and is not stale.
   */
  isFresh(query: Q) {
    return this.queryMapper(query).some((descriptor) => {
      const key = this.keyFunction(descriptor);
      const entry = this._cache.get(key);
      return entry && !this.isEntryStale(descriptor, entry);
    });
  }

  /**
   * Subscribe to the cache.
   *
   * This will call the provided `listener`  with data from the cache as soon
   * as some is available, and then call it again whenever data in the cache is
   * updated.
   *
   * @returns a function to call to stop receiving updates for this query.  This
   *   must be called, or we'll query forever!
   *
   * @example
   *     const unsubscribe = cache.subscribe((err, events) => console.log(events));
   *     unsubscribe(); // Call when done with the subscription.
   */
  subscribe(
    query: Q,
    listener: (err: Error | undefined, result: R | undefined) => void,
  ): () => void {
    const cacheKeys = this.queryMapper(query);

    const id = nextSubscriberId++;
    if (nextSubscriberId >= Number.MAX_SAFE_INTEGER) {
      nextSubscriberId = 0;
    }

    const subscriber: Subscriber<E, Q, D, R> = {
      id: `${this.name}-${id}`,
      active: true,
      query,
      listener,
      cacheKeys,
      keys: cacheKeys.map(this.keyFunction),
      latestFetchTimeSent: undefined,
      lastCacheEntries: undefined,
    };

    this._subscribers.push(subscriber);

    // Check to see if results are in the cache and update cacheHits/cacheMisses.
    // We don't want to update these every time we push new results.
    const { allInCache } = this._getFromCache(subscriber.cacheKeys);
    if (allInCache) {
      this.cacheHits++;
    } else {
      this.cacheMisses++;
    }

    const doQuery = () => {
      if (!subscriber.active) {
        return;
      }

      // Schedule an update when the data next needs to be revalidated.
      this._updateSubscriber(subscriber).then(
        () => repeat(),
        (err) => {
          listener(err, undefined);
          repeat({ force: true });
        },
      );
    };

    // Called to set a timer to fetch data again.
    const repeat = (options: { force?: boolean } = {}) => {
      if (!subscriber.active) {
        return;
      }

      let revalidationTimeout: number | undefined;
      if (!subscriber.lastCacheEntries || options.force) {
        revalidationTimeout = ERROR_RETRY_INTERVAL;
      } else if (subscriber.lastCacheEntries) {
        for (let index = 0; index < subscriber.cacheKeys.length; index++) {
          const descriptor = subscriber.cacheKeys[index];
          const entry = subscriber.lastCacheEntries[index];
          const timeout = this._getNextFetchTimeout(descriptor, entry);
          if (timeout !== undefined && (!revalidationTimeout || timeout < revalidationTimeout)) {
            revalidationTimeout = timeout;
          }
        }
      }

      if (revalidationTimeout !== undefined) {
        setTimeout(doQuery, revalidationTimeout);
      }
    };

    // Do the initial query.
    doQuery();

    // Return the "unsubscribe" function.
    return () => {
      subscriber.active = false;
      this._subscribers = this._subscribers.filter((s) => s !== subscriber);
    };
  }

  private async _updateSubscriber(subscriber: Subscriber<E, Q, D, R>) {
    const freshResults = await this._getFromServer(
      subscriber.cacheKeys,
      subscriber.lastCacheEntries,
    );
    this._sendResultsToSubscriber(subscriber, freshResults);
  }

  // Send results to the subscriber.
  private _sendResultsToSubscriber(
    subscriber: Subscriber<E, Q, D, R>,
    cacheEntries: (CacheEntry<E> | undefined)[],
  ) {
    let latestFetchTime = 0;
    for (const cacheEntry of cacheEntries) {
      if (cacheEntry && cacheEntry.fetchTime > latestFetchTime) {
        latestFetchTime = cacheEntry.fetchTime;
      }
    }

    // Only send if these are new results.
    if (subscriber.active && subscriber.latestFetchTimeSent !== latestFetchTime) {
      subscriber.latestFetchTimeSent = latestFetchTime;
      subscriber.lastCacheEntries = cacheEntries;

      const definedEntries = cacheEntries.filter((e) => !!e) as CacheEntry<E>[];
      if (definedEntries.length && definedEntries.every((e) => e.state === 'error')) {
        // If all our cache entries are in "error" state,
        // then call the listener with one of the errors.
        const err = (definedEntries[0] as CacheEntry<E> & { state: 'error' }).err;
        subscriber.listener(err, undefined);
      } else {
        // As long as we have at least some results, return them.
        const result = this._mapResults(subscriber.query, cacheEntries);
        subscriber.listener(undefined, result);
      }
    }
  }

  private _mapResults(query: Q, cacheEntries: (CacheEntry<E> | undefined)[]) {
    const definedEntries = cacheEntries.flatMap((e) =>
      e && e.state === 'cached' ? [e.entry] : [],
    );
    return this.resultMapper(query, definedEntries);
  }

  /**
   * Get data from the cache.
   */
  private _get(query: Q) {
    const cacheKeys = this.queryMapper(query);

    let cachedResultsPromise: Promise<(CacheEntry<E> | undefined)[]> | undefined;

    const { allInCache, cacheEntries, stale } = this._getFromCache(cacheKeys);
    if (allInCache) {
      this.cacheHits++;
      cachedResultsPromise = Promise.resolve(cacheEntries);
    } else {
      this.cacheMisses++;
    }

    // If we need to fetch fresh data from the cache, do so.
    if (!allInCache || stale || !cachedResultsPromise) {
      const fetchLatest = this._getFromServer(cacheKeys, undefined);
      if (!cachedResultsPromise) {
        cachedResultsPromise = fetchLatest;
      } else {
        // Since we're not going to return `fetchLatest`, no one will listen
        // to any errors from this promise - listen here, and ignore them, since
        // we're just trying to revalidate the cache.
        fetchLatest.catch(() => void 0);
      }
    }

    return { stale, cacheEntries, cacheKeys, cachedResultsPromise };
  }

  /**
   * Get data from the cache.
   */
  async get(query: Q): Promise<R> {
    const { cachedResultsPromise } = this._get(query);
    const cacheEntries = await cachedResultsPromise;
    return this._mapResults(query, cacheEntries);
  }

  /**
   * Get data from the cache immediately.  If results are not in the cache, this
   * will start workers to fetch them, but data from any missing cache entries
   * will not be returned.  If some cache entries are missing, this will return
   * results from the entries we have.
   */
  getSync(query: Q): { stale: boolean; result: R } {
    const { cachedResultsPromise, cacheEntries, stale } = this._get(query);

    // If fetching new entries fails, don't care.
    cachedResultsPromise.catch(() => void 0);

    return { stale, result: this._mapResults(query, cacheEntries) };
  }

  /**
   * Fetch a raw entry from the underlying cache by its key.  Returns undefined
   * if there is no entry for the key.
   */
  getEntry(key: string): { entry: E | undefined; isLoading: boolean } {
    const cacheEntry = this._cache.get(key);
    const entry = cacheEntry?.state === 'cached' ? cacheEntry.entry : undefined;
    return {
      entry,
      isLoading: cacheEntry?.state === 'error' || !!this._cacheWorkers[key],
    };
  }

  /**
   * This can be called to manually update an existing entry in the cache.
   *
   * Note that if the cache entry is currently being refreshed (i.e. if
   * `getEntry()` returns `{ loading: true }`) then any updates you make will
   * be overwritten by the pending refresh.
   *
   * Any subscribers for this key will be called immediately with the new update.
   */
  updateEntry(key: string, cacheData: E) {
    this.lastUpdate = Date.now();
    this._cache.set(key, {
      fetchTime: Date.now(),
      state: 'cached',
      validatedTime: Date.now(),
      entry: cacheData,
    });

    for (const subscriber of this._subscribers) {
      if (subscriber.keys.includes(key)) {
        this._updateSubscriber(subscriber).catch(() => {
          /* Do nothing */
        });
      }
    }
  }

  /**
   * Throw away all data in the cache.  This will cause all subscribers to fetch
   * fresh data.
   */
  invalidate() {
    this._cache.clear();

    // Cancel all running cache workers.
    this._cacheWorkers = {};

    for (const subscriber of this._subscribers) {
      subscriber.lastCacheEntries = undefined;
      subscriber.latestFetchTimeSent = 0;
      this._updateSubscriber(subscriber).catch(() => {
        /* Do nothing */
      });
    }
  }

  private _startCacheWorker(
    key: string,
    descriptor: D,
    cacheData: CacheEntry<E> | undefined,
  ): Promise<CacheEntry<E>> {
    // If there's already a cache worker working on this query, just return
    // the existing worker.
    if (!this._cacheWorkers[key]) {
      const newCachePromise = cacheData
        ? this._refreshExistingCacheEntry(descriptor, cacheData)
        : this._fetchNewCacheEntry(descriptor);

      // Always remove the promise from `_cacheWorkers` when we are done.
      const cacheWorker = newCachePromise.then(
        (newCacheEntry) => {
          if (this._cacheWorkers[key] === cacheWorker) {
            this.lastUpdate = Date.now();
            this._cache.set(key, newCacheEntry);
            delete this._cacheWorkers[key];
            return newCacheEntry;
          }
          throw new Error('Cache worker cancelled');
        },
        (err) => {
          delete this._cacheWorkers[key];
          throw err;
        },
      );

      this._cacheWorkers[key] = cacheWorker;
    }

    return this._cacheWorkers[key];
  }

  private async _fetchNewCacheEntry(descriptor: D) {
    let newCacheEntry: CacheEntry<E>;
    try {
      const entry = await this.fetchFunction(descriptor, undefined);
      newCacheEntry = {
        fetchTime: Date.now(),
        state: 'cached',
        validatedTime: Date.now(),
        entry,
      };
    } catch (err) {
      newCacheEntry = {
        fetchTime: Date.now(),
        state: 'error',
        err: err as Error,
        lastErrorDelay: INITIAL_EXPONENTIAL_BACK_OFF_ERROR_INTERVAL,
      };
    }
    return newCacheEntry;
  }

  /**
   * This will check to see of a given cache entry is up to date.  If
   * the server has no new data, this will return a new cache entry with the
   * exact same entry object and a new fetch time.  If there's new data available,
   * this will fetch the new data.
   */
  private async _refreshExistingCacheEntry(
    descriptor: D,
    cacheData: CacheEntry<E>,
  ): Promise<CacheEntry<E>> {
    let newResult: CacheEntry<E>;

    try {
      const existingEntry = cacheData.state === 'cached' ? cacheData.entry : undefined;
      const cacheEntry = await this.fetchFunction(descriptor, existingEntry);

      if (cacheData.state === 'cached' && cacheEntry === cacheData.entry) {
        // Data is up to date.  Return the `newResult` object with an
        // updated validatedTime.
        newResult = {
          ...cacheData,
          validatedTime: Date.now(),
        };
      } else {
        this.cacheRefreshes++;
        newResult = {
          fetchTime: Date.now(),
          state: 'cached',
          validatedTime: Date.now(),
          entry: cacheEntry,
        };
      }
    } catch (err) {
      console.error(`Error revalidating ${this.name}`, err);
      if (cacheData.state === 'cached') {
        // On an error, update the `validatedTime`, otherwise as soon as we next
        // read from this cache we'll try to fetch again.  If this error is because
        // the server is down or our token is no good, this will lead to us constantly
        // polling the server.
        newResult = {
          ...cacheData,
          validatedTime: Date.now(),
        };
      } else {
        // The result was already an error - double the `lastErrorDelay` so we'll
        // wait a bit before trying again.
        newResult = {
          fetchTime: Date.now(),
          state: 'error',
          err: err as Error,
          lastErrorDelay: Math.min(
            cacheData.lastErrorDelay * 2,
            MAX_EXPONENTIAL_BACK_OFF_ERROR_INTERVAL,
          ),
        };
      }
    }

    return newResult;
  }

  /**
   * Fetch values from the cache.
   *
   * @param cacheDescriptors - Descriptors for the cache entries to retrieve.
   * @returns { allInCache, cacheEntires, stale } - allInCache will be true if all entries
   *   are in the cache, false otherwise.
   */
  private _getFromCache(cacheDescriptors: D[]): {
    allInCache: boolean;
    cacheEntries: (CacheEntry<E> | undefined)[];
    stale: boolean;
  } {
    const cacheEntries: (CacheEntry<E> | undefined)[] = [];
    let allInCache = true;
    let stale = false;

    for (const descriptor of cacheDescriptors) {
      const key = this.keyFunction(descriptor);
      const cacheEntry = this._cache.get(key);

      if (!cacheEntry) {
        cacheEntries.push(undefined);
        allInCache = false;
      } else {
        cacheEntries.push(cacheEntry);
        if (this.isEntryStale(descriptor, cacheEntry)) {
          stale = true;
        }
      }
    }

    return { allInCache, cacheEntries, stale: stale || !allInCache };
  }

  /**
   * Fetch values from the cache if available, from the server otherwise..
   *
   * @param cacheDescriptors - Descriptors for the cache entries to retrieve.
   * @param existingEntries - If entries were previously fetched, this is the
   *   set of previous entries.  It's possible, if we have too many subscribers,
   *   that we'll evict entries from the LRUCache even if a subscriber is using
   *   them - passing the previous entries ensures we still don't have to go
   *   to the server, even if the LRU cache is overwhelmed.
   */
  private _getFromServer(
    cacheDescriptors: D[],
    existingEntries: (CacheEntry<E> | undefined)[] | undefined,
  ) {
    const results: Promise<CacheEntry<E>>[] = [];

    for (let index = 0; index < cacheDescriptors.length; index++) {
      const descriptor = cacheDescriptors[index];

      const key = this.keyFunction(descriptor);
      const cacheEntry = this._cache.get(key) || existingEntries?.[index];

      if (!cacheEntry || this.isEntryStale(descriptor, cacheEntry)) {
        const workerPromise = this._startCacheWorker(key, descriptor, cacheEntry);
        results.push(workerPromise);
      } else {
        results.push(Promise.resolve(cacheEntry));
      }
    }

    return Promise.all(results);
  }

  private isEntryStale(descriptor: D, entry: CacheEntry<E> | undefined): boolean {
    const nextFetchTimeout = this._getNextFetchTimeout(descriptor, entry);
    return nextFetchTimeout !== undefined && nextFetchTimeout <= 0;
  }

  /**
   * Returns the number of milliseconds until we are allowed to fetch this entry.
   * If this returned undefined, we should never fetch this entry.
   */
  private _getNextFetchTimeout(descriptor: D, entry?: CacheEntry<E>) {
    if (!entry) {
      return 0;
    }
    let lastValidatedTime = entry.fetchTime;
    if (entry.state === 'cached') {
      lastValidatedTime = entry.validatedTime;
    }

    const elapsed = Date.now() - lastValidatedTime;
    let timeout: number | undefined;
    if (entry.state === 'cached') {
      timeout = this.getRevalidationTimeout(descriptor, entry.entry);
    } else {
      timeout = entry.lastErrorDelay;
    }

    if (timeout === undefined) {
      return undefined;
    }
    return Math.max(0, timeout - elapsed);
  }
}
